import { useEffect, useMemo, useState, useRef } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  Search,
  ArrowUpRight,
  MapPin,
  X,
  Leaf,
  Flame,
  Landmark,
  Users,
  Heart,
} from "lucide-react";
import { CATEGORIES, AUTH_SIGNUP_URL } from "@/lib/data";
import { MFT, SP } from "@/components/site/BrandLinks";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ease = [0.22, 1, 0.36, 1];

const CEM_ILLUSTRATION =
  "https://customer-assets-lxgj4vgw.emergentagent.net/job_memory-preserve-8/artifacts/oljvubvj_cemetaries-illustration.jpg";

const PLATE = {
  gate:
    "https://images.unsplash.com/photo-1541185934-01b600ea069c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
  overgrown:
    "https://images.unsplash.com/photo-1509644851169-2acc08aa25b5?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
  mausoleum:
    "https://images.unsplash.com/photo-1476514525535-07fb3b4ae5f1?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
  angel:
    "https://images.unsplash.com/photo-1518709594023-6eab9bab7b23?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
  candles:
    "https://images.unsplash.com/photo-1477414348463-c0eb7f1359b6?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
  memorial:
    CATEGORIES.find((c) => c.slug === "memorials")?.image,
  obit:
    CATEGORIES.find((c) => c.slug === "obituaries")?.image,
  celebrate:
    CATEGORIES.find((c) => c.slug === "celebrate-a-life")?.image,
};

/* ------------------------------------------------------------------ */
/*  Editorial primitives                                                */
/* ------------------------------------------------------------------ */

function Chapter({ n, title, subtitle, children }) {
  return (
    <motion.section
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.9, ease }}
      className="max-w-[1600px] mx-auto px-6 md:px-10 py-14 md:py-20 border-t border-[color:var(--ink-line)]"
    >
      <div className="grid grid-cols-12 gap-6 md:gap-10 mb-10 md:mb-14 items-end">
        <div className="col-span-12 md:col-span-2">
          <div className="numeric-index text-[color:var(--ink-soft)]">{n}</div>
        </div>
        <div className="col-span-12 md:col-span-10">
          <h2
            className="font-serif-display leading-[0.95] tracking-tight"
            style={{ fontSize: "clamp(30px, 4vw, 58px)" }}
          >
            {title}
            {subtitle ? (
              <>
                {" "}
                <span className="font-italic-serif italic text-[color:var(--ink-soft)]">
                  {subtitle}
                </span>
              </>
            ) : null}
          </h2>
        </div>
      </div>
      <div className="grid grid-cols-12 gap-6 md:gap-10">
        <div className="hidden md:block md:col-span-2" />
        <div className="col-span-12 md:col-span-10">{children}</div>
      </div>
    </motion.section>
  );
}

const Body = ({ children }) => (
  <p className="text-[17px] md:text-[19px] leading-[1.65] max-w-[62ch] text-[color:var(--ink)]">
    {children}
  </p>
);

const Prose = ({ children }) => (
  <div className="space-y-6 md:space-y-7">{children}</div>
);

function Ledger({ items, columns = 2 }) {
  const colClass = columns === 3 ? "md:grid-cols-3" : "md:grid-cols-2";
  return (
    <ul
      className={`mt-2 grid grid-cols-1 ${colClass} gap-x-10 gap-y-0 border-t border-[color:var(--ink-line)]`}
    >
      {items.map((item, i) => (
        <li
          key={i}
          className="flex items-baseline gap-4 py-3 border-b border-[color:var(--ink-line)]"
        >
          <span className="numeric-index text-[color:var(--ink-soft)] w-8 shrink-0">
            {String(i + 1).padStart(2, "0")}
          </span>
          <span
            className="font-serif-display leading-[1.15]"
            style={{ fontSize: "clamp(17px, 1.5vw, 22px)" }}
          >
            {item}
          </span>
        </li>
      ))}
    </ul>
  );
}

function Plate({ src, caption, caption2, aspect = "aspect-[4/5]" }) {
  return (
    <figure className="artifact border border-[color:var(--ink-line-strong)] overflow-hidden">
      <div className={`${aspect} overflow-hidden bg-[color:var(--bone-2)]`}>
        <img src={src} alt="" className="zoom w-full h-full object-cover" loading="lazy" />
      </div>
      <figcaption className="p-4 border-t border-[color:var(--ink-line)] flex items-baseline justify-between gap-4">
        <span className="numeric-index text-[color:var(--ink-soft)]">{caption}</span>
        <span className="numeric-index text-[color:var(--ink-soft)] text-right">{caption2}</span>
      </figcaption>
    </figure>
  );
}

/* ------------------------------------------------------------------ */
/*  Content                                                             */
/* ------------------------------------------------------------------ */

const HISTORIC = [
  "Early settlers and pioneers",
  "Community founders",
  "Veterans and military heroes",
  "Religious leaders",
  "Teachers and scholars",
  "Artists and musicians",
  "Business leaders",
  "Public servants",
  "Families whose stories shaped entire regions",
];

const DIGITAL = [
  "Life stories",
  "Family relationships",
  "Photographs",
  "Videos",
  "Voice recordings",
  "Letters and journals",
  "Awards and achievements",
  "Military service",
  "Community contributions",
  "Family memories",
  "Guest tributes",
  "Important anniversaries",
  "Burial or memorial locations",
  "Historical documents",
];

const MAPPING = [
  "Cemetery and mausoleum names",
  "Geographic location",
  "Maps and directions",
  "Historical significance",
  "Family plots",
  "Memorial gardens",
  "Monument photographs",
  "Nearby heritage sites",
  "Local history",
  "Visiting information",
  "Community stories",
];

const CONTRIBUTIONS = [
  "Memories",
  "Stories",
  "Photographs",
  "Videos",
  "Audio recordings",
  "Historical research",
  "Genealogical records",
  "Newspaper articles",
  "Family documents",
  "Obituaries",
  "Tributes and condolences",
];

const CELEBRATE_LINES = [
  "The laughter they shared.",
  "The wisdom they offered.",
  "The traditions they preserved.",
  "The kindness they showed.",
  "The dreams they pursued.",
  "The lives they touched.",
];

const PILLARS = [
  { icon: Leaf,     label: "Preserve Memories",  desc: "Record life stories, photos and cherished moments" },
  { icon: Flame,    label: "Honor Traditions",   desc: "Remember anniversaries, customs and acts of love" },
  { icon: Landmark, label: "Document History",   desc: "Explore cemetery & mausoleum heritage and records" },
  { icon: Users,    label: "Share & Connect",    desc: "Invite family to keep memories alive together" },
  { icon: Heart,    label: "Celebrate a Life",   desc: "Turn remembrance into a tribute that inspires" },
];

/* ------------------------------------------------------------------ */
/*  Page                                                                */
/* ------------------------------------------------------------------ */

export default function Cemeteries() {
  /* -------- Directory state (search + filters) -------- */
  const [index, setIndex] = useState({ countries: [], total: 0 });
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [country, setCountry] = useState("");
  const [state, setState] = useState("");
  const [city, setCity] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/cemeteries/index`).then((r) => setIndex(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const params = {};
        if (q.trim()) params.q = q.trim();
        if (country) params.country = country;
        if (state) params.state = state;
        if (city) params.city = city;
        const r = await axios.get(`${API}/cemeteries`, { params });
        setItems(r.data.results || []);
      } catch (e) {
        setItems([]);
      } finally {
        setLoading(false);
      }
    }, 200);
    return () => clearTimeout(t);
  }, [q, country, state, city]);

  const currentCountry = useMemo(
    () => index.countries.find((c) => c.country === country),
    [index, country]
  );
  const currentState = useMemo(
    () => currentCountry?.states.find((s) => s.state === state),
    [currentCountry, state]
  );

  const clearAll = () => {
    setQ("");
    setCountry("");
    setState("");
    setCity("");
  };

  const activeFilters = [country, state, city, q].filter(Boolean).length;

  /* -------- Hero parallax -------- */
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const heroY = useTransform(scrollYProgress, [0, 1], ["0%", "16%"]);

  return (
    <main data-testid="cemeteries-page" className="pt-32 md:pt-40">
      {/* ============================================================ */}
      {/*  Editorial article                                              */}
      {/* ============================================================ */}

      {/* Masthead */}
      <section ref={heroRef} className="max-w-[1600px] mx-auto px-6 md:px-10 pb-10 md:pb-16">
        <div className="flex items-center gap-3 text-[13px] mb-8">
          <Link to="/" className="link-underline text-[color:var(--ink-soft)]">
            Index
          </Link>
          <span className="text-[color:var(--ink-soft)]">/</span>
          <span className="numeric-index">Vol. III · Cemeteries & Mausoleums</span>
        </div>

        <div className="overline text-[color:var(--rust)] mb-4">§ Cemeteries & Mausoleums</div>
        <motion.h1
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.1, ease }}
          style={{ y: heroY, fontSize: "clamp(48px, 9vw, 170px)" }}
          className="font-serif-display leading-[0.9] tracking-[-0.03em] max-w-6xl"
        >
          Sacred places{" "}
          <span className="font-italic-serif italic text-[color:var(--ink-soft)]">
            where memories live forever.
          </span>
        </motion.h1>
      </section>

      {/* Pull-quote */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-10 pb-10 md:pb-16">
        <div className="border-y border-[color:var(--ink)] py-10 md:py-16">
          <div className="grid grid-cols-12 gap-6 md:gap-10 items-center">
            <div className="col-span-1">
              <span
                className="font-serif-display text-[color:var(--rust)] leading-none"
                style={{ fontSize: "clamp(60px, 8vw, 120px)" }}
              >
                “
              </span>
            </div>
            <div className="col-span-11">
              <p
                className="font-italic-serif italic leading-[1.15]"
                style={{ fontSize: "clamp(22px, 3.6vw, 52px)" }}
              >
                A cemetery is not merely a place of farewell — it is a place
                of remembrance. A mausoleum is not simply a monument — it is a
                testament to lives that continue to inspire those who follow.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Opening prose (two paragraphs before the illustration) */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-10 pb-16 md:pb-20">
        <div className="grid grid-cols-12 gap-6 md:gap-10">
          <div className="hidden md:block md:col-span-2">
            <div className="overline text-[color:var(--ink-soft)]">
              § A place where love endures
            </div>
          </div>
          <div className="col-span-12 md:col-span-10">
            <Prose>
              <p
                className="font-serif-display leading-[1.15]"
                style={{ fontSize: "clamp(22px, 2.4vw, 34px)" }}
              >
                Every life leaves footprints upon the world.
              </p>
              <Body>
                Some are remembered through stories passed from one generation
                to the next. Others live on in photographs, handwritten
                letters, cherished heirlooms, and the values they instilled in
                their families.
              </Body>
              <Body>
                Cemeteries and mausoleums are among humanity's oldest places
                of remembrance. They are peaceful spaces where families gather
                to honour loved ones, reflect on lives well lived, and
                maintain a connection that extends beyond generations. The
                Cemeteries & Mausoleums section of <MFT />, part of the <SP />{" "}
                platform, is dedicated to preserving these sacred places and
                the stories they hold — a respectful digital archive where
                families can remember, document, and celebrate the lives of
                those who came before them.
              </Body>
            </Prose>
          </div>
        </div>
      </section>

      {/* Featured illustration */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-10 pb-16 md:pb-24">
        <motion.figure
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 1.1, ease }}
          className="relative max-w-5xl mx-auto"
        >
          <div className="border border-[color:var(--ink-line-strong)] p-3 md:p-5 bg-[color:var(--bone-2)]/50">
            <div className="overflow-hidden">
              <img
                src={CEM_ILLUSTRATION}
                alt="A cinematic dusk illustration of a family walking through a peaceful cemetery with a mausoleum inscribed 'Forever in our hearts', a marble angel statue, a dove in flight, an open photo album with a portrait, lit candles, a headstone reading 'In Loving Memory', and five icons naming Preserve Memories, Honor Traditions, Document History, Share & Connect, Celebrate a Life."
                className="w-full h-auto block"
                loading="lazy"
                data-testid="cemeteries-illustration"
              />
            </div>
          </div>
          <figcaption className="mt-4 flex items-start justify-between gap-6">
            <div className="numeric-index text-[color:var(--ink-soft)]">
              Plate № C · Where memories rest · Where legacies live on
            </div>
            <div className="numeric-index text-[color:var(--ink-soft)] text-right max-w-[300px] hidden md:block">
              For our past · For our future · For our family
            </div>
          </figcaption>
        </motion.figure>
      </section>

      {/* Chapter 01 · More Than Resting Places */}
      <Chapter n="01" title="More than" subtitle="resting places.">
        <Prose>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 md:gap-10">
            <div className="lg:col-span-7">
              <Body>
                Across cultures and civilisations, people have created places
                to honour the departed.
              </Body>
              <div className="border-l border-[color:var(--ink)] pl-6 md:pl-8 space-y-3 py-2 my-4">
                {[
                  "Some are simple graves beneath a quiet tree.",
                  "Others are beautifully designed mausoleums, memorial gardens, family vaults, churchyards, temple grounds, or historic cemeteries that have stood for centuries.",
                ].map((line, i) => (
                  <p
                    key={i}
                    className="font-italic-serif italic leading-[1.4]"
                    style={{ fontSize: "clamp(18px, 1.8vw, 24px)" }}
                  >
                    {line}
                  </p>
                ))}
              </div>
              <Body>These places remind us that every life mattered.</Body>
              <Body>
                Behind every name engraved in stone is a story of hope,
                sacrifice, love, family, friendship, and community. Every
                marker represents a chapter in the larger story of humanity.
              </Body>
            </div>
            <div className="hidden lg:block lg:col-span-5">
              <Plate
                src={PLATE.gate}
                caption="Plate № 001"
                caption2="An iron gate, kept ajar"
              />
            </div>
          </div>
        </Prose>
      </Chapter>

      {/* Chapter 02 · A Living Connection Between Generations */}
      <Chapter n="02" title="A living connection" subtitle="between generations.">
        <Prose>
          <Body>
            When families visit the resting places of their ancestors, they
            are doing more than paying respects — they are continuing a
            conversation across generations.
          </Body>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-10 py-4">
            {[
              { k: "Children", v: "learn the names of grandparents they never met." },
              { k: "Families", v: "remember anniversaries and milestones." },
              { k: "Stories", v: "are shared that might otherwise be forgotten." },
              { k: "Values", v: "are passed from one generation to the next." },
            ].map((r, i) => (
              <div key={i} className="border-t border-[color:var(--ink-line)] pt-4">
                <div className="numeric-index text-[color:var(--ink-soft)] mb-1">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <p
                  className="font-serif-display leading-[1.15]"
                  style={{ fontSize: "clamp(22px, 2.3vw, 32px)" }}
                >
                  {r.k}{" "}
                  <span className="font-italic-serif italic text-[color:var(--ink-soft)]">
                    {r.v}
                  </span>
                </p>
              </div>
            ))}
          </div>
          <Body>
            These quiet moments help strengthen the bonds between the past,
            the present, and the future.
          </Body>
        </Prose>
      </Chapter>

      {/* Chapter 03 · Honouring Every Tradition */}
      <Chapter n="03" title="Honouring" subtitle="every tradition.">
        <Prose>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 md:gap-10">
            <div className="hidden lg:block lg:col-span-5">
              <Plate
                src={PLATE.candles}
                caption="Plate № 002"
                caption2="Candles, lit at dusk"
              />
            </div>
            <div className="lg:col-span-7">
              <Body>
                Every culture and faith has its own meaningful ways of
                remembering loved ones.
              </Body>
              <Body>
                Some families visit cemeteries on anniversaries or special
                occasions. Others gather for prayers, place flowers, light
                candles or lamps, offer wreaths, or simply spend a few moments
                in silent reflection.
              </Body>
              <Body>
                Many traditions include acts of remembrance such as planting
                trees, sharing meals, offering charity, reciting prayers,
                singing hymns, or telling stories about the person being
                remembered.
              </Body>
              <Body>
                <MFT /> honours these diverse customs with respect, recognising
                that while traditions vary around the world, the desire to
                remember and celebrate loved ones is universal.
              </Body>
            </div>
          </div>
        </Prose>
      </Chapter>

      {/* Chapter 04 · Preserving Historic Cemeteries */}
      <Chapter n="04" title="Preserving" subtitle="historic cemeteries.">
        <Prose>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 md:gap-10">
            <div className="lg:col-span-7">
              <Body>
                Many cemeteries are also important historical archives. Their
                monuments tell the story of communities, migrations, wars,
                epidemics, architecture, art, and changing societies.
              </Body>
              <Body>Historic cemeteries often preserve:</Body>
              <Ledger items={HISTORIC} columns={2} />
              <p
                className="font-italic-serif italic text-[color:var(--ink-soft)] leading-[1.3] mt-6"
                style={{ fontSize: "clamp(20px, 2vw, 28px)" }}
              >
                Each inscription becomes a valuable piece of local and family
                history.
              </p>
            </div>
            <div className="hidden lg:block lg:col-span-5">
              <Plate
                src={PLATE.overgrown}
                caption="Plate № 003"
                caption2="Where moss keeps the record"
              />
            </div>
          </div>
        </Prose>
      </Chapter>

      {/* Chapter 05 · Family Mausoleums */}
      <Chapter n="05" title="Family" subtitle="mausoleums.">
        <Prose>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 md:gap-10">
            <div className="hidden lg:block lg:col-span-5">
              <Plate
                src={PLATE.mausoleum}
                caption="Plate № 004"
                caption2="A house built for memory"
              />
            </div>
            <div className="lg:col-span-7">
              <Body>
                Throughout history, many families have built mausoleums as
                lasting symbols of remembrance.
              </Body>
              <Body>
                Whether modest or grand, these structures represent love,
                respect, and the desire to preserve family history.
              </Body>
              <p
                className="font-italic-serif italic text-[color:var(--rust)] leading-[1.3] mt-4"
                style={{ fontSize: "clamp(22px, 2.4vw, 32px)" }}
              >
                They often become gathering places where future generations
                learn about their ancestors and appreciate the lives that
                helped build the family they know today.
              </p>
            </div>
          </div>
        </Prose>
      </Chapter>

      {/* Chapter 06 · Digital Memorials */}
      <Chapter n="06" title="Digital memorials for" subtitle="future generations.">
        <Prose>
          <Body>Time can weather stone.</Body>
          <div className="border-l border-[color:var(--ink)] pl-6 md:pl-8 space-y-3 py-2 my-2">
            {[
              "Photographs may fade.",
              "Documents can be lost.",
              "Stories may disappear if they are never recorded.",
            ].map((line, i) => (
              <p
                key={i}
                className="font-italic-serif italic leading-[1.4]"
                style={{ fontSize: "clamp(18px, 1.8vw, 24px)" }}
              >
                {line}
              </p>
            ))}
          </div>
          <Body>
            <MFT /> helps preserve these memories by creating respectful digital
            memorials that complement physical resting places. Families can
            document:
          </Body>
          <Ledger items={DIGITAL} columns={3} />
          <Body>
            Together, these become an enduring tribute that can be shared with
            future generations anywhere in the world.
          </Body>
        </Prose>
      </Chapter>

      {/* Chapter 07 · Mapping Places of Remembrance */}
      <Chapter n="07" title="Mapping places" subtitle="of remembrance.">
        <Prose>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 md:gap-10">
            <div className="lg:col-span-7">
              <Body>Every memorial site has a story to tell.</Body>
              <Body>
                <MFT /> allows families to preserve important details,
                including:
              </Body>
              <Ledger items={MAPPING} columns={2} />
              <Body>
                By connecting these places to family trees, future generations
                gain a deeper understanding of their roots.
              </Body>
            </div>
            <div className="hidden lg:block lg:col-span-5">
              <Plate
                src={PLATE.angel}
                caption="Plate № 005"
                caption2="An angel, still keeping watch"
              />
            </div>
          </div>
        </Prose>
      </Chapter>

      {/* Chapter 08 · Remembering Together */}
      <Chapter n="08" title="Remembering" subtitle="together.">
        <Prose>
          <Body>Remembrance is often strongest when shared.</Body>
          <Body>Family members can contribute:</Body>
          <Ledger items={CONTRIBUTIONS} columns={3} />
          <p
            className="font-italic-serif italic text-[color:var(--ink-soft)] leading-[1.3]"
            style={{ fontSize: "clamp(20px, 2vw, 28px)" }}
          >
            Each contribution enriches the family's collective memory and
            ensures that no life is reduced to a name and a date.
          </p>
        </Prose>
      </Chapter>

      {/* Chapter 09 · Celebrate a Life */}
      <Chapter n="09" title="Celebrate" subtitle="a life.">
        <Prose>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 md:gap-10">
            <div className="lg:col-span-7">
              <Body>
                The Cemeteries & Mausoleums section works hand in hand with{" "}
                <Link
                  to="/category/celebrate-a-life"
                  className="link-underline font-medium"
                  data-testid="cem-celebrate-link"
                >
                  Celebrate a Life
                </Link>
                , allowing every memorial to become a celebration of the
                person's journey. Rather than focusing solely on loss,
                families can remember:
              </Body>
              <div className="border-l border-[color:var(--rust)] pl-6 md:pl-8 space-y-3 py-2 my-4">
                {CELEBRATE_LINES.map((line, i) => (
                  <p
                    key={i}
                    className="font-italic-serif italic leading-[1.4]"
                    style={{ fontSize: "clamp(18px, 1.8vw, 26px)" }}
                  >
                    {line}
                  </p>
                ))}
              </div>
              <Body>
                In doing so, remembrance becomes an act of gratitude as much
                as reflection.
              </Body>
            </div>
            <div className="hidden lg:block lg:col-span-5">
              <Plate
                src={PLATE.celebrate}
                caption="Plate № 006"
                caption2="A life, retold"
              />
            </div>
          </div>
        </Prose>
      </Chapter>

      {/* Chapter 10 · A Legacy That Never Fades */}
      <Chapter n="10" title="A legacy" subtitle="that never fades.">
        <Prose>
          <Body>Every family carries the responsibility of remembering.</Body>
          <Body>
            Not only for those who have passed, but for those who will come
            after us.
          </Body>
          <Body>
            One day, children and grandchildren may stand before a memorial
            wondering who that person was. Imagine if, with a simple click,
            they could discover photographs, hear stories, see handwritten
            letters, watch family videos, and understand the remarkable life
            behind the inscription.
          </Body>
          <div className="border-l border-[color:var(--ink)] pl-6 md:pl-8 space-y-3 py-2 my-2">
            {[
              "That is the vision of MyFamilyTree.",
              "To transform places of remembrance into places of discovery.",
              "To ensure that names become stories.",
              "That stories become legacies.",
              "And that legacies continue to inspire generations yet to come.",
            ].map((line, i) => (
              <p
                key={i}
                className="font-italic-serif italic leading-[1.4]"
                style={{ fontSize: "clamp(18px, 1.9vw, 26px)" }}
              >
                {line}
              </p>
            ))}
          </div>
        </Prose>
      </Chapter>

      {/* Five pillars strip — mirrors the illustration */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-10 py-20 md:py-24 border-t border-[color:var(--ink-line)]">
        <div className="overline text-[color:var(--ink-soft)] mb-8">
          § Remember · Honour · Preserve
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-0 border-l border-t border-[color:var(--ink-line)]">
          {PILLARS.map((p, i) => {
            const Icon = p.icon;
            return (
              <motion.div
                key={p.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.6, delay: i * 0.06, ease }}
                className="border-r border-b border-[color:var(--ink-line)] p-6 md:p-7 min-h-[220px] flex flex-col justify-between bg-[color:var(--bone)] hover:bg-[color:var(--bone-2)]"
                style={{ transition: "background-color 400ms ease" }}
                data-testid={`cem-pillar-${p.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <div className="w-11 h-11 rounded-full border border-[color:var(--ink)] flex items-center justify-center text-[color:var(--rust)]">
                  <Icon size={20} />
                </div>
                <div>
                  <div
                    className="font-serif-display leading-[1] mt-6"
                    style={{ fontSize: "clamp(20px, 1.9vw, 26px)" }}
                  >
                    {p.label}
                  </div>
                  <p className="mt-2 text-[13px] leading-[1.5] text-[color:var(--ink-soft)]">
                    {p.desc}
                  </p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* Closing manifesto */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-10 py-24 md:py-28 border-t border-[color:var(--ink)]">
        <div className="overline text-[color:var(--ink-soft)] mb-6">
          § Where every stone marks a story
        </div>
        <p
          className="font-serif-display leading-[1.02] max-w-5xl mb-10"
          style={{ fontSize: "clamp(30px, 4vw, 62px)" }}
        >
          Every memorial preserves a legacy.{" "}
          <span className="font-italic-serif italic text-[color:var(--ink-soft)]">
            Every life continues to inspire.
          </span>
        </p>
        <Body>
          Within <MFT />, every resting place becomes more than a location —
          it becomes a lasting bridge between generations, ensuring that the
          people we love are remembered not only for when they lived, but for
          how they lived and the lives they enriched.
        </Body>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-10 mt-12">
          {[
            "Every name has a story. Every story is a legacy.",
            "Remember · Honour · Preserve. Today · Tomorrow · Always.",
            "For our past. For our future. For our family.",
          ].map((line, i) => (
            <div key={i} className="border-t border-[color:var(--ink)] pt-4">
              <div className="numeric-index text-[color:var(--ink-soft)] mb-2">
                {String(i + 1).padStart(2, "0")}
              </div>
              <p
                className="font-serif-display leading-[1.15]"
                style={{ fontSize: "clamp(20px, 2vw, 28px)" }}
              >
                {line}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* ============================================================ */}
      {/*  The Cemeteries Directory (search + filters + ledger)          */}
      {/* ============================================================ */}
      <section
        id="directory"
        data-testid="cemeteries-directory"
        className="max-w-[1600px] mx-auto px-6 md:px-10 pt-24 md:pt-32 border-t border-[color:var(--ink)]"
      >
        <div className="overline text-[color:var(--rust)] mb-4">§ The Directory</div>
        <div className="grid grid-cols-12 gap-6 md:gap-10 items-end mb-10">
          <div className="col-span-12 md:col-span-8">
            <h2
              className="font-serif-display leading-[0.95] tracking-tight"
              style={{ fontSize: "clamp(40px, 6vw, 96px)" }}
            >
              Search cemeteries{" "}
              <span className="font-italic-serif italic text-[color:var(--ink-soft)]">
                around the world.
              </span>
            </h2>
          </div>
          <div className="col-span-12 md:col-span-4">
            <p className="text-[15px] leading-[1.65] text-[color:var(--ink-soft)] max-w-md">
              A cross-border index of cemeteries, memorial parks and burial
              grounds — searchable by country, state and city, each linked
              to its authoritative public record for the next step in your
              research.
            </p>
            <div className="mt-6 numeric-index text-[color:var(--ink-soft)]">
              {index.total || items.length} cemeteries indexed · growing weekly
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-[1600px] mx-auto px-6 md:px-10 pb-8">
        <div className="border border-[color:var(--ink)] p-1 flex items-center bg-[color:var(--bone)]">
          <Search size={20} className="ml-4 text-[color:var(--ink-soft)]" />
          <input
            data-testid="cemetery-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by cemetery, city, family name or denomination…"
            className="flex-1 bg-transparent px-4 py-4 text-[16px] md:text-[18px] focus:outline-none placeholder:text-[color:var(--ink-soft)]"
          />
          {activeFilters > 0 && (
            <button
              onClick={clearAll}
              data-testid="cemetery-clear"
              className="mr-2 flex items-center gap-1 text-[12px] text-[color:var(--ink-soft)] hover:text-[color:var(--rust)] px-3 py-2"
            >
              Clear <X size={14} />
            </button>
          )}
          <span className="numeric-index text-[color:var(--ink-soft)] pr-4 hidden md:inline">
            {loading ? "…" : `${items.length} result${items.length === 1 ? "" : "s"}`}
          </span>
        </div>

        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <div className="overline text-[color:var(--ink-soft)] mb-2">Country</div>
            <select
              data-testid="cemetery-country"
              value={country}
              onChange={(e) => {
                setCountry(e.target.value);
                setState("");
                setCity("");
              }}
              className="w-full bg-transparent border border-[color:var(--ink-line-strong)] px-3 py-3 text-[14px] focus:outline-none focus:border-[color:var(--ink)]"
            >
              <option value="">All countries</option>
              {index.countries.map((c) => (
                <option key={c.country_code} value={c.country}>
                  {c.country} · {c.total}
                </option>
              ))}
            </select>
          </div>
          <div>
            <div className="overline text-[color:var(--ink-soft)] mb-2">State / Province</div>
            <select
              data-testid="cemetery-state"
              value={state}
              onChange={(e) => {
                setState(e.target.value);
                setCity("");
              }}
              disabled={!currentCountry}
              className="w-full bg-transparent border border-[color:var(--ink-line-strong)] px-3 py-3 text-[14px] focus:outline-none focus:border-[color:var(--ink)] disabled:opacity-40"
            >
              <option value="">All states</option>
              {currentCountry?.states.map((s) => (
                <option key={s.state} value={s.state}>
                  {s.state}
                </option>
              ))}
            </select>
          </div>
          <div>
            <div className="overline text-[color:var(--ink-soft)] mb-2">City</div>
            <select
              data-testid="cemetery-city"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              disabled={!currentState}
              className="w-full bg-transparent border border-[color:var(--ink-line-strong)] px-3 py-3 text-[14px] focus:outline-none focus:border-[color:var(--ink)] disabled:opacity-40"
            >
              <option value="">All cities</option>
              {currentState?.cities.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>
        </div>
      </section>

      <section className="max-w-[1600px] mx-auto px-6 md:px-10 pb-24 md:pb-32">
        <div className="rule-strong mt-6" />
        {items.length === 0 && !loading && (
          <div className="py-16 text-center">
            <p
              className="font-italic-serif italic text-[color:var(--ink-soft)]"
              style={{ fontSize: "clamp(20px, 2vw, 28px)" }}
            >
              No cemeteries match this search. Try widening the filters.
            </p>
          </div>
        )}
        {items.map((c, i) => (
          <motion.a
            key={c.id}
            href={c.external}
            target="_blank"
            rel="noopener noreferrer"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.6, delay: (i % 8) * 0.03, ease }}
            className="group grid grid-cols-12 gap-3 md:gap-6 py-6 md:py-8 border-b border-[color:var(--ink-line)] items-baseline hover:pl-3"
            style={{ transition: "padding-left 500ms cubic-bezier(0.7,0,0.3,1)" }}
            data-testid={`cemetery-row-${c.id}`}
          >
            <div className="col-span-2 md:col-span-1 numeric-index text-[color:var(--ink-soft)]">
              {String(i + 1).padStart(3, "0")}
            </div>
            <div className="col-span-10 md:col-span-5">
              <h3
                className="font-serif-display leading-[1.05] group-hover:text-[color:var(--rust)]"
                style={{ fontSize: "clamp(20px, 2.2vw, 34px)", transition: "color 400ms ease" }}
              >
                {c.name}
              </h3>
              <div className="mt-2 flex items-center gap-2 text-[13px] text-[color:var(--ink-soft)]">
                <MapPin size={13} />
                <span>{c.city} · {c.state} · {c.country}</span>
              </div>
            </div>
            <div className="col-span-6 md:col-span-3 text-[13px] text-[color:var(--ink-soft)] leading-[1.5]">
              {c.notable}
            </div>
            <div className="col-span-4 md:col-span-2 numeric-index text-[color:var(--ink-soft)]">
              <div>Est. {c.established}</div>
              <div className="mt-1">{c.records}</div>
            </div>
            <div className="col-span-2 md:col-span-1 flex justify-end items-baseline">
              <span className="hidden md:inline numeric-index text-[color:var(--ink-soft)] mr-2 group-hover:text-[color:var(--rust)]">
                {c.external_label}
              </span>
              <ArrowUpRight
                size={20}
                className="transition-transform duration-500 group-hover:rotate-45 group-hover:text-[color:var(--rust)]"
              />
            </div>
          </motion.a>
        ))}
      </section>

      {/* Contribute CTA */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-10 pb-24">
        <div className="border border-[color:var(--ink)] p-8 md:p-14 grid grid-cols-12 gap-6 items-end">
          <div className="col-span-12 md:col-span-8">
            <div className="overline text-[color:var(--ink-soft)] mb-3">§ Contribute</div>
            <h3
              className="font-serif-display leading-[0.98]"
              style={{ fontSize: "clamp(28px, 3.6vw, 54px)" }}
            >
              Know a cemetery we haven't listed?{" "}
              <span className="font-italic-serif italic text-[color:var(--ink-soft)]">
                Send us its name.
              </span>
            </h3>
          </div>
          <div className="col-span-12 md:col-span-4 md:text-right space-x-2">
            <Link to="/contact" className="btn-rust" data-testid="cem-contribute-cta">
              Submit a cemetery <span aria-hidden>→</span>
            </Link>
          </div>
        </div>
      </section>

      {/* Signature */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-10 pb-32">
        <div className="border-t border-[color:var(--ink-line)] pt-10">
          <div className="grid grid-cols-12 gap-6 md:gap-10 items-end">
            <div className="col-span-12 md:col-span-7">
              <div
                className="font-serif-display leading-[0.95]"
                style={{ fontSize: "clamp(38px, 5.5vw, 90px)" }}
              >
                Cemeteries
                <span className="text-[color:var(--rust)]"> · </span>
                <span className="font-italic-serif italic text-[color:var(--ink-soft)]">
                  Mausoleums
                </span>
              </div>
              <div
                className="font-italic-serif italic mt-3 text-[color:var(--ink-soft)]"
                style={{ fontSize: "clamp(16px, 1.7vw, 22px)" }}
              >
                Where every stone marks a story · Every memorial preserves a
                legacy · Every life continues to inspire.
              </div>
            </div>
            <div className="col-span-12 md:col-span-5 md:text-right">
              <a
                href={AUTH_SIGNUP_URL}
                className="btn-rust"
                data-testid="cem-cta-primary"
              >
                Begin a memorial archive <span aria-hidden>→</span>
              </a>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
